import { BASE_URL_WS } from '@partsbadger/utils';
import React, { useEffect, useState } from 'react';
import { Alert } from 'antd';
import QuoteStore from '../stores/QuoteStore';
import { observer } from 'mobx-react';
import ReconnectingWebSocket from 'reconnecting-websocket';
import { runInAction } from 'mobx';
import { createReconnectRefresh } from './socketReconnect';

// Upload/analysis progress can arrive many times per second across several parts.
// Apply buffered events together so the form re-renders once per batch, not per event.
const SOCKET_BATCH_MS = 100;
// After an outage every open form reconnects within seconds: spread their full quote requests.
const REFRESH_JITTER_MS = 5000;
// A short drop (or the first connection) is not worth a notice.
const NOTICE_DELAY_MS = 4000;

type SocketEvent = { type: string; message: string };

const SocketConnection = () => {
    const { quote, isConnectedToSocket } = QuoteStore;
    const [noticeDue, setNoticeDue] = useState(false);

    // The notice only shows after the socket has been down for a while.
    useEffect(() => {
        if (isConnectedToSocket) {
            setNoticeDue(false);
            return undefined;
        }
        const timer = setTimeout(() => setNoticeDue(true), NOTICE_DELAY_MS);
        return () => clearTimeout(timer);
    }, [isConnectedToSocket]);

    useEffect(() => {
        const url = `${BASE_URL_WS}/ws/instant-quote/${quote?.uid}/`;

        const ws = new ReconnectingWebSocket(url, [], {
            // Never give up: a dropped socket must come back by itself, however long the server was away.
            // The delay grows to 20 s so a struggling server is not hammered by every open form.
            maxRetries: Infinity,
            maxReconnectionDelay: 20000,
        });

        // The browser knows when the network is back: do not wait for the next scheduled attempt.
        const reconnectNow = () => {
            if (ws.readyState !== WebSocket.OPEN) ws.reconnect();
        };
        window.addEventListener('online', reconnectNow);

        const reconnect = createReconnectRefresh(
            () => {
                void QuoteStore.refreshQuote();
            },
            () => Math.random() * REFRESH_JITTER_MS
        );

        ws.onopen = () => {
            QuoteStore.setConnectedSocket(true);
            reconnect.onOpen();
        };

        let pending: SocketEvent[] = [];
        let flushTimer: ReturnType<typeof setTimeout> | null = null;

        const applyEvent = (data: SocketEvent) => {
            if (data.type === 'updated.quote_line_item' || data.type == 'updated_part') {
                QuoteStore.setLineItem(JSON.parse(data.message));
            }

            if (data.type === 'conversion_progress') {
                const payload = JSON.parse(data.message);
                QuoteStore.updateConversionProgress(payload.line_item_pk, payload.progress);
            }
        };

        const flush = () => {
            flushTimer = null;
            const batch = pending;
            pending = [];
            // Within a batch only the latest progress per line item matters.
            const latestProgress = new Map<string, number>();
            batch.forEach((data, index) => {
                if (data.type === 'conversion_progress') {
                    try {
                        latestProgress.set(String(JSON.parse(data.message).line_item_pk), index);
                    } catch {
                        // Malformed payloads are reported when applied below.
                    }
                }
            });
            runInAction(() => {
                batch.forEach((data, index) => {
                    try {
                        if (data.type === 'conversion_progress') {
                            const pk = String(JSON.parse(data.message).line_item_pk);
                            if (latestProgress.get(pk) !== index) return;
                        }
                        applyEvent(data);
                    } catch (err) {
                        console.error('SocketConnection Error - Message.data content:', data, 'Origin Error:', err);
                    }
                });
            });
        };

        ws.onmessage = message => {
            try {
                pending.push(JSON.parse(message.data));
            } catch (err) {
                console.error('SocketConnection Error - Message.data content:', message?.data, 'Origin Error:', err);
                return;
            }
            if (!flushTimer) {
                flushTimer = setTimeout(flush, SOCKET_BATCH_MS);
            }
        };

        ws.onclose = () => {
            QuoteStore.setConnectedSocket(false);
            reconnect.onClose();
        };

        return () => {
            window.removeEventListener('online', reconnectNow);
            if (flushTimer) {
                clearTimeout(flushTimer);
            }
            if (ws) {
                ws.close();
            }
        };
    }, []);

    return (
        <>
            {noticeDue && !isConnectedToSocket ? (
                <Alert
                    banner
                    showIcon
                    type="warning"
                    data-testid="socket-notice"
                    style={{ marginBottom: 8 }}
                    message="Live updates paused, reconnecting… The page keeps checking for changes meanwhile."
                />
            ) : null}
            <div
                style={{
                    fontSize: 18,
                    marginRight: 0,
                    color: 'transparent',
                }}
            >
                •
            </div>
        </>
    );
};

export default observer(SocketConnection);
